import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../shared/api/client";
import { downtimeReasonNeedsApproval, type DowntimeReasonDraft, type DowntimeReasonTarget } from "./downtime-reason-queue";
import { productionGateway } from "./production-gateway";
import { downtimeReasonRequest, type DowntimeReasonRequest } from "./downtime-reason-submission";
export type ReasonSubmitOptions = { signal: AbortSignal; expectedSessionId: string };
type Supervisor = { id: string; name: string };
export function useDowntimeReasonApproval({ target, endedAt, terminalCode, sessionId, saving, submit }: {
  target: DowntimeReasonTarget; endedAt: string; terminalCode: string; sessionId: string; saving: boolean;
  submit: (input: DowntimeReasonRequest, approvalToken: string | undefined, options: ReasonSubmitOptions) => Promise<unknown>;
}) {
  const scope = `${sessionId}|${target.runId}|${target.downtimeId}`;
  const active = useRef({ scope, live: true, sequence: 0 });
  const inFlight = useRef<AbortController | null>(null);
  const [approvalRequired, setApprovalRequired] = useState(() => downtimeReasonNeedsApproval(endedAt));
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]), [supervisorId, setSupervisorId] = useState("");
  const [supervisorPin, setSupervisorPin] = useState("");
  const [loadingSupervisors, setLoadingSupervisors] = useState(false), [approvalSaving, setApprovalSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null), [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    active.current = { scope, live: true, sequence: 0 };
    return () => { active.current.live = false; active.current.sequence++; inFlight.current?.abort(); };
  }, [scope]);
  useEffect(() => {
    if (!approvalRequired) return;
    let disposed = false;
    const controller = new AbortController(), timer = window.setTimeout(() => controller.abort(), 15_000);
    setLoadingSupervisors(true);
    void productionGateway.terminalSupervisors(terminalCode, controller.signal).then((items) => {
      if (disposed || controller.signal.aborted || !active.current.live) return;
      setSupervisors(items); setSupervisorId((previous) => items.some((item) => item.id === previous) ? previous : items[0]?.id ?? "");
      if (!items.length) setFormError("Aktif vardiya amiri bulunamadı. Sistem yöneticisine başvurun.");
    }).catch((error: unknown) => {
      if (disposed || !active.current.live) return;
      setSupervisors([]); setSupervisorId("");
      setFormError(controller.signal.aborted ? "Amir listesi zamanında alınamadı. Modalı kapatabilir veya yeniden deneyebilirsiniz." : error instanceof Error ? error.message : "Amir listesi alınamadı.");
    }).finally(() => { window.clearTimeout(timer); if (!disposed && active.current.live) setLoadingSupervisors(false); });
    return () => { disposed = true; controller.abort(); window.clearTimeout(timer); };
  }, [approvalRequired, loadAttempt, terminalCode, scope]);
  const cancel = () => { active.current.sequence++; inFlight.current?.abort(); inFlight.current = null; setSupervisorPin(""); setApprovalSaving(false); };
  const save = async (draft: DowntimeReasonDraft, noteRequired: boolean, intentId: string): Promise<boolean> => {
    const input = downtimeReasonRequest(draft, intentId);
    if (saving || inFlight.current || !input || (noteRequired && !draft.note.trim())) return false;
    const controller = new AbortController(); inFlight.current = controller;
    const sequence = ++active.current.sequence;
    const current = () => active.current.live && active.current.scope === scope && active.current.sequence === sequence && !controller.signal.aborted;
    const timer = window.setTimeout(() => controller.abort(), 20_000);
    setApprovalSaving(true); setFormError(null);
    try {
      let approvalToken: string | undefined;
      if (approvalRequired || downtimeReasonNeedsApproval(endedAt)) {
        const supervisor = supervisors.find((item) => item.id === supervisorId), pin = supervisorPin;
        setSupervisorPin(""); // Clear before any network await; never persist/log PIN or token.
        if (!supervisor || !/^\d{4}$/.test(pin)) { setFormError("Amiri seçin ve amirin dört haneli PIN’ini girin."); return false; }
        const approval = await productionGateway.approveTerminalAction(terminalCode, {
          supervisorId: supervisor.id, pin, actionScope: "BACKDATED_DOWNTIME_CORRECTION",
          targetRef: `${target.runId}|${target.downtimeId}`, ...input, expectedSessionId: sessionId,
        }, controller.signal);
        if (!current()) return false;
        approvalToken = approval.approvalToken;
      }
      if (!current()) return false;
      await submit(input, approvalToken, { signal: controller.signal, expectedSessionId: sessionId });
      return current();
    } catch (error) {
      if (!active.current.live || active.current.sequence !== sequence) return false;
      if (error instanceof ApiError && error.code === "DOWNTIME_REASON_APPROVAL_REQUIRED") {
        setApprovalRequired(true); setFormError("Bu kayıt için amir onayı gerekiyor. Seçiminiz ve açıklamanız korundu; amir kendi PIN’ini girmeli.");
      } else setFormError(controller.signal.aborted ? "İşlemin sonucu doğrulanamadı. Taslak korundu. Ekranı yenileyerek kayıt durumunu kontrol edin; işlem otomatik tekrarlanmadı."
        : error instanceof Error ? error.message : "Duruş nedeni doğrulanamadı.");
      return false;
    } finally {
      window.clearTimeout(timer);
      if (inFlight.current === controller) inFlight.current = null;
      if (active.current.live && active.current.sequence === sequence) setApprovalSaving(false);
    }
  };
  return { approvalRequired, supervisors, supervisorId, supervisorPin, setSupervisorPin,
    selectSupervisor: (id: string) => { setSupervisorId(id); setSupervisorPin(""); setFormError(null); },
    loadingSupervisors, approvalSaving, formError, cancel, retrySupervisors: () => { setFormError(null); setLoadAttempt((value) => value + 1); }, save };
}
